import { Box, IconButton } from '@mui/material';
import { parseGeocoderComponents } from '@realadvisor/hooks';
import { MapPin, X } from 'lucide-react';

import { GmapsAutocomplete } from '@modules/_shared/components/gmaps-autocomplete/GmapsAutocomplete';
import { useLocale } from '@shared/hooks/locale';

export type HomeAddressFields = {
  home_address_route: string | null;
  home_address_street_number: string | null;
  home_address_postcode: string | null;
  home_address_locality: string | null;
  home_address_state: string | null;
  home_address_country_code: string | null;
  home_address_lat: number | null;
  home_address_lng: number | null;
};

type AddressEditFormProps = {
  loading: boolean;
  onSubmit: (fields: HomeAddressFields) => void;
  onCancel: () => void;
};

export const AddressEditForm = ({
  loading,
  onSubmit,
  onCancel,
}: AddressEditFormProps) => {
  const { t, countryCode } = useLocale();

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      background: 'var(--mui-palette-background-paper)',
      borderRadius: '8px',
    },
    '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall .MuiInputBase-input.MuiAutocomplete-input':
      {
        paddingTop: 0,
        paddingBottom: 0,
      },
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          color: 'text.secondary',
          display: 'flex',
          flexShrink: 0,
          width: 16,
          mt: '7px',
        }}
      >
        <MapPin size={16} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <GmapsAutocomplete
          size="small"
          fullWidth
          disabled={loading}
          countryRestriction={countryCode}
          sx={inputSx}
          onChange={result => {
            const components = parseGeocoderComponents(
              result.address_components,
            );
            onSubmit({
              home_address_route: components.route ?? null,
              home_address_street_number: components.streetNumber ?? null,
              home_address_postcode: components.postcode ?? null,
              home_address_locality: components.locality ?? null,
              home_address_state: components.state ?? null,
              home_address_country_code: components.countryCode ?? null,
              home_address_lat: result.geometry.location.lat(),
              home_address_lng: result.geometry.location.lng(),
            });
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', flexShrink: 0, pt: '2px' }}>
        <IconButton
          size="small"
          onClick={onCancel}
          disabled={loading}
          sx={{
            width: 28,
            height: 28,
            color: 'text.secondary',
            '&:hover': { backgroundColor: 'transparent' },
          }}
          title={t('Cancel')}
        >
          <X size={16} />
        </IconButton>
      </Box>
    </Box>
  );
};
